import React, { Fragment, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Stack } from '@mui/material';
import { IntlProvider } from 'react-intl';

// MODULES
import useLoginRequest from './requests/LoginRequest';
import useHomeRequest from './requests/HomeRequest';
import useAdminRequest from './requests/AdminRequest';

// COMPONENTS
import ErrorModal from './components/common/modals/ErrorModal';
import Header from './components/layouts/Header';
import Loader from './components/common/Loader';

// ROUTING
import AppRoutes from './routes/Routes';

// STYLES
import CloseSVG from './images/close';

// UTILS
import config from './Config';
import localeData from './locales/data.json';

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessLevel, setAccessLevel] = useState(null);
  const [accessType, setAccessType] = useState(null);
  const [user, setUser] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [timezone, setTimezone] = useState(null);
  const [dedicatedNumbers, setDedicatedNumbers] = useState([]);
  const [acting_names, setActingNames] = useState({
    provider_name: null,
    customer_name: null,
  });
  const [language, setLanguage] = useState('en');
  const [isMobile, setIsMobile] = useState(false);
  const [windowLocation, setWindowLocation] = useState(null);
  const [infoStack, setInfoStack] = useState([]);
  const [errorStack, setErrorStack] = useState([]);
  const [showInfo, setShowInfo] = useState(false);
  const [showError, setShowError] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const { updateActingProvider, updateActingCustomer } = useAdminRequest();
  const { getUser, getSessionData } = useHomeRequest();
  const { login, logout } = useLoginRequest();

  /*
    Updates the access_level and access_state state vars
  */
  const updateAccessDetails = (accessLevel, accessType) => {
    setAccessLevel(accessLevel); // base user = -1, Sales Agent = 0, Help desk rep = 1, Accounts & reports = 2, admin = 3
    setAccessType(accessType); // base user = 0, customer admin = 1, provider admin = 2, god admin = 3
  };

  const removeError = () => {
    setShowError(false);
  };

  const removeInfo = () => {
    setShowInfo(false);
  };

  const toggleMenuDisplay = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleWindowSizeChange = () => {
    setIsMobile(window.innerWidth <= 600);
  };

  const resetWindowLocation = () => {
    setShowError(false);
    setWindowLocation(window.location.pathname);
  };

  const displayInfo = () => {
    // If no errors, return empty array
    if (window.location.pathname !== windowLocation) {
      setShowInfo(false);
      setWindowLocation(window.location.pathname);
    }
    if (infoStack && infoStack.length > 0) {
      console.log('infoStack - here: ', infoStack);
      // Otherwise, print component
      return (
        <div className={showInfo ? 'info-card' : 'info-card hide-error'}>
          <p className="info-message float-left">
            <b>Info: </b>
            {infoStack}
            <a
              href="#0"
              className="close-button float-right"
              onClick={() => removeInfo()}
            >
              <CloseSVG />
            </a>
          </p>
        </div>
      );
    }
  };

  const handleInfo = (info) => {
    console.log('The notification is: ' + info);
    setInfoStack(info);
    setShowInfo(true);
  };

  /**
   * Clears imitations of customer/provider and re-renders the display.
   */
  const handleImitationClear = async () => {
    await updateActingCustomerDetails('default');
  };

  const updateActingProviderDetails = async (providerId) => {
    // TODO: merge this and customer detail function
    const response = await updateActingProvider(providerId, handleError);

    if (response) {
      const { acting_provider_name, acting_customer_name } = response;
      setActingNames({
        provider_name: acting_provider_name,
        customer_name: acting_customer_name,
      });
    }
  };

  const updateActingCustomerDetails = async (customerId) => {
    const response = await updateActingCustomer(customerId, handleError);

    if (response) {
      const { acting_provider_name, acting_customer_name } = response;
      setActingNames({
        provider_name: acting_provider_name,
        customer_name: acting_customer_name,
      });
    }
  };

  const handleError = (error) => {
    // Handle log out upon hitting web session error.
    if (
      error === 'Web Session Error' ||
      error === 'Web Session has expired' ||
      error === 'No Token Found.'
    ) {
      handleLogout();
      return;
    }
    console.log('The error is: ' + error);
    setErrorStack(error);
    setShowError(true);
  };

  /*
  Convenience function to handle logout.
  */
  const handleLogout = async () => {
    await logout(localStorage.getItem('rox_user_email'), handleError);

    localStorage.removeItem('rox_user_token');
    localStorage.removeItem('rox_user_email');

    // TODO: look into converting this into navigate. Would have to clear state though...
    window.location.href = '/';
    setIsAuthenticated(false);
  };

  /*
  Updates whether a User has or has not authenticated
  */
  const userHasAuthenticated = (authenticated, authDetails) => {
    setIsAuthenticated(authenticated);
    setAccessLevel(authDetails.accessLevel); // base user = -1, Sales Agent = 0, Help desk rep = 1, Accounts & reports = 2, admin = 3
    setAccessType(authDetails.accessType); // base user = 0, customer admin = 1, provider admin = 2, god admin = 3
    setActingNames({
      provider_name: authDetails.acting_provider_name,
      customer_name: authDetails.acting_customer_name,
    });

    if (authenticated) {
      refreshUser();
    }
  };

  const refreshUser = async () => {
    try {
      let userData = await getUser(handleError);
      setCustomer(userData.customer);
      setUser(userData.user);
      setTimezone(userData.timezone);
      setDedicatedNumbers(userData.dedicatedNumbers);
    } catch (e) {
      handleError(e);
    }
  };

  const commonProps = {
    customer: customer,
    user: user,
    isAuthenticated: isAuthenticated,
    userHasAuthenticated: userHasAuthenticated,
    accessLevel: accessLevel,
    accessType: accessType,
    dateFormats: config.dateFormats,
    errorHandler: handleError,
    infoHandler: handleInfo,
    navigate: navigate,
    location: location,
    isMobile: isMobile,
    acting_names: acting_names,
  };

  const nonWebSmsProps = {
    ...commonProps,
    removeError: removeError,
    updateAccessDetails: updateAccessDetails,
    updateActingProviderDetails: updateActingProviderDetails,
    updateActingCustomerDetails: updateActingCustomerDetails,
  };
  const webSmsProps = {
    ...commonProps,
    loading: loading,
    refreshUser: refreshUser,
    mobileMenuOpen: mobileMenuOpen,
    toggleMobileMenu: toggleMenuDisplay,
    dedicatedNumbers: dedicatedNumbers,
  };

  useEffect(() => {
    const _handleError = (error) => {
      // Handle log out upon hitting web session error.
      if (
        error === 'Web Session Error' ||
        error === 'Web Session has expired' ||
        error === 'No Token Found.'
      ) {
        _handleLogout();
        return;
      }
      console.log('The error is: ' + error);
      setErrorStack(error);
      setShowError(true);
    };

    /*
    Convenience function to handle logout.
    */
    const _handleLogout = async () => {
      await logout(localStorage.getItem('rox_user_email'), _handleError);

      localStorage.removeItem('rox_user_token');
      localStorage.removeItem('rox_user_email');

      // TODO: look into converting this into navigate. Would have to clear state though...
      window.location.href = '/';
      setIsAuthenticated(false);
    };

    async function getData() {
      let token = await localStorage.getItem('rox_user_token');
      setWindowLocation(window.location.pathname);
      handleWindowSizeChange();
      window.addEventListener('resize', handleWindowSizeChange);

      // have token?
      if (token) {
        let userData, customer, user, sessionData;

        try {
          //auth state
          sessionData = await getSessionData(_handleError);
          userData = await getUser(_handleError);
        } catch (error) {
          // Handle offline errors here
          console.log('hit: ', error);
          // navigate to offline page

          localStorage.removeItem('rox_user_token');
          localStorage.removeItem('rox_user_email');

          // Head back to the login page
          window.location.href = '/';
        }

        //trigger logout
        if (typeof userData === 'undefined') {
          customer = null;
          user = null;
        } else {
          customer = userData.customer;

          user = userData.user;
        }
        if (sessionData?.redox_cookie) {
          try {
            let redox_cookie = JSON.parse(sessionData.redox_cookie);
            setActingNames({
              provider_name: redox_cookie.acting_provider_name,
              customer_name: redox_cookie.acting_customer_name,
            });
          } catch (e) {
            console.error();
          }
        }

        setAccessLevel(sessionData?.access_level || -1); // base user = -1, Sales Agent = 0, Help desk rep = 1, Accounts & reports = 2, admin = 3
        setAccessType(sessionData?.access_type || 0); // base user = 0, customer admin = 1, provider admin = 2, god admin = 3
        setUser(user);
        setCustomer(customer);
        setTimezone(userData.timezone);
        setDedicatedNumbers(userData.dedicatedNumbers);
        setLanguage('en'); // TODO: I believe language is to be implemented later on.
        setMobileMenuOpen(false);
      }
      setLoading(false);
    }

    getData();

    let handleStorageTimeout;
    const handleStorage = () => {
      if (handleStorageTimeout) {
        clearTimeout(handleStorageTimeout);
      }

      handleStorageTimeout = setTimeout(() => {
        window.location.reload();
      }, 300);
    };

    window.addEventListener('storage', () => handleStorage());

    return () => {
      window.removeEventListener('storage', () => handleStorage());
    };
  }, []);

  // Try full locale, try locale without region code, fallback to 'en'
  const messages = localeData[language];

  // If loading, render spinner
  if (loading) {
    return (
      <IntlProvider locale={language} messages={messages}>
        <Stack
          sx={{
            width: '100%',
            height: '100dvh',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Loader />
        </Stack>
      </IntlProvider>
    );

    // Else load pages such as login or register that does not need the navbar.
  } else if (!customer) {
    return (
      <IntlProvider locale={language} messages={messages}>
        <AppRoutes
          nonWebSmsProps={nonWebSmsProps}
          webSmsProps={webSmsProps}
          errorHandler={() => handleError.bind(this)}
          infoHandler={() => handleInfo.bind(this)}
        />
      </IntlProvider>
    );
  } else {
    return (
      <IntlProvider locale={language} messages={messages}>
        <Fragment>
          <Header
            accessLevel={accessLevel}
            accessType={accessType}
            navBarInfo={{
              userFirstName: user?.user_first_name,
              userEmail: user?.user_email,
              customerName: customer?.customer_name,
              customerAccountNo: customer?.customer_account_no,
              dedicatedNumbers: dedicatedNumbers,
              timezone: timezone,
            }}
            actingNames={acting_names}
            handleImitationClear={handleImitationClear}
            handleLogout={handleLogout}
          />
          {displayInfo()}
          <ErrorModal
            errorStack={errorStack}
            windowLocation={windowLocation}
            showError={showError}
            removeError={removeError}
            resetWindowLocation={resetWindowLocation}
          />
          <AppRoutes
            nonWebSmsProps={nonWebSmsProps}
            webSmsProps={webSmsProps}
            errorHandler={() => handleError.bind(this)}
            infoHandler={() => handleInfo.bind(this)}
          />
        </Fragment>
      </IntlProvider>
    );
  }
}
